import { Trans, useLingui } from "@lingui/react/macro";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Container,
  type ContainerProps,
  Divider,
  lighten,
  Link as MuiLink,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router";
import { useAppConfig, useCampground } from "@/contexts/AppConfigContext";
import { useFeatureFlags } from "@/contexts/FeaturesContext";
import { analytics } from "@/utils/analytics";
import { isStaticPagePath } from "@/utils/routes";
import CookieConsentController from "./CookieConsentController";
import LanguageSwitcher from "./LanguageSwitcher";

interface AppLayoutProps {
  children: ReactNode;
  contentMaxWidth?: ContainerProps["maxWidth"];
  title?: ReactNode;
  showAppBar?: boolean;
  centerContent?: boolean;
}

function AppLayout({
  children,
  contentMaxWidth = "lg",
  title,
  showAppBar = true,
  centerContent = false,
}: AppLayoutProps) {
  const { t } = useLingui();
  const params = useParams<{ language?: string; campground?: string }>();
  const { data: campgroundData } = useCampground();
  const {
    showCampnerdLabel,
    affiliateLogoUrl,
    imprintUrl,
    privacyPolicyUrl,
    isEmbedded,
    usesExternalConsent,
  } = useAppConfig();
  const { flags } = useFeatureFlags();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  const homePath = search
    ? `/${params.language}/${params.campground}/?${search}`
    : `/${params.language}/${params.campground}/`;
  const savedReturnTo = (location.state as { returnTo?: unknown } | null)?.returnTo;
  const staticPageLinkState = {
    returnTo:
      typeof savedReturnTo === "string" && isStaticPagePath(location.pathname)
        ? savedReturnTo
        : `${location.pathname}${location.search}${location.hash}`,
  };
  const theme = useTheme();

  const appBar = (
    <AppBar position="sticky" color="transparent" elevation={0} className="no-print">
      <Toolbar
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          height: { xs: 64, sm: 100 },
          px: { xs: 2, sm: 7 },
          bgcolor: { xs: "background.paper", sm: "background.default" },
        }}
        disableGutters
      >
        <Link to={homePath} style={{ display: "flex", alignItems: "center" }}>
          {campgroundData.urls?.image && (
            <Box
              component="img"
              src={campgroundData.urls.image}
              alt={t`CampNerd`}
              sx={{ maxHeight: { xs: 45, sm: 80 } }}
            />
          )}
        </Link>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={{ xs: 0, sm: 1.5 }}
        >
          <Typography
            variant="h2"
            sx={{ textAlign: "center", display: { xs: "none", sm: "block" } }}
          >
            {title || campgroundData.campground?.name}
          </Typography>
          {flags.demoMode && (
            <Chip label="Demo mode" color="warning" sx={{ p: 0, width: "auto" }} />
          )}
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <LanguageSwitcher />
        </Box>
      </Toolbar>
      <Divider />
      {title && (
        <Box
          sx={{
            display: { xs: "block", sm: "none", overflowX: "auto", scrollbarWidth: "none" },
            bgcolor: "background.paper",
          }}
        >
          {title}
        </Box>
      )}
    </AppBar>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { sm: isEmbedded ? 0 : "100vh" },
      }}
    >
      {showAppBar && appBar}
      <CookieConsentController />
      <Box
        sx={{
          flex: 1,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: centerContent ? "center" : "start",
        }}
      >
        <Container maxWidth={contentMaxWidth} sx={{ px: 0 }}>
          {children}
        </Container>
      </Box>
      {showCampnerdLabel && (
        <Box
          sx={{
            width: "100%",
            bgcolor: lighten(theme.palette.primary.main, 0.85),
            py: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Avatar
            src={affiliateLogoUrl ?? "/images/campnerd_logo_no_text.svg"}
            alt="CampNerd"
            sx={{ width: 20, height: 20 }}
          />
          <Typography variant="body2">
            <Trans>Your booking is handled by CampNerd</Trans>
          </Typography>
        </Box>
      )}
      <Box
        component="footer"
        className="no-print"
        sx={{
          pb: 4,
          px: 7,
          py: 2,
          mt: "auto",
          textAlign: "center",
          display: { xs: "none", sm: "flex" },
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          CampNerd GmbH & Co KG, Menzelstraße 28, 12157 Berlin Deutschland | Handelsregisternummer:
          HRA 60637
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {privacyPolicyUrl ? (
            <MuiLink
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
            >
              <Trans>Privacy Policy</Trans>
            </MuiLink>
          ) : (
            <MuiLink
              component={Link}
              to={`/${params.language}/${params.campground}/privacy-policy`}
              state={staticPageLinkState}
              color="inherit"
            >
              <Trans>Privacy Policy</Trans>
            </MuiLink>
          )}
          {" | "}
          {imprintUrl ? (
            <MuiLink href={imprintUrl} target="_blank" rel="noopener noreferrer" color="inherit">
              <Trans>Imprint</Trans>
            </MuiLink>
          ) : (
            <MuiLink
              component={Link}
              to={`/${params.language}/${params.campground}/imprint`}
              state={staticPageLinkState}
              color="inherit"
            >
              <Trans>Imprint</Trans>
            </MuiLink>
          )}
          {" | "}
          <MuiLink
            component={Link}
            to={`/${params.language}/${params.campground}/faq`}
            state={staticPageLinkState}
            color="inherit"
          >
            <Trans>FAQ</Trans>
          </MuiLink>
          {!usesExternalConsent && (
            <>
              {" | "}
              <MuiLink
                component="button"
                type="button"
                onClick={() => analytics.resetTrackingConsent()}
                color="inherit"
                sx={{ font: "inherit", cursor: "pointer" }}
              >
                <Trans>Cookie settings</Trans>
              </MuiLink>
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

export default AppLayout;
